import React from 'react';
import styled from 'styled-components';
import myxss from 'react-form-builder2/lib/form-elements/myxss';

export interface ChoiceOption {
  value: string;
  text: string;
  /* Rich-text label authored in the builder. `text` stays the plain-text
     mirror, so options saved before rich labels existed still render. */
  html?: string;
  key: string;
}

/* draft-js wraps every block in a <p>, which would break the inline label
   layout. Unwrap a lone block down to its inline markup and keep anything
   more complex as-is. Sanitised with the same filter the library uses for
   element labels, since this markup is authored in the builder and rendered
   with dangerouslySetInnerHTML. */
const labelMarkup = (option: ChoiceOption): string => {
  const raw = option.html?.trim();
  if (!raw) return option.text || '';
  const html = myxss.process(raw);
  const blocks = html.match(/<p[^>]*>[\s\S]*?<\/p>/gi);
  if (blocks && blocks.length === 1) {
    return blocks[0].replace(/^<p[^>]*>/i, '').replace(/<\/p>$/i, '');
  }
  return html;
};

const StyledWrapper = styled.div`
  .choice-option {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 6px 0;
  }

  &.is-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
  }

  &.is-inline .choice-option {
    margin: 6px 0;
  }

  .choice-option input {
    width: 15px;
    height: 15px;
    margin: 3px 0 0;
    flex-shrink: 0;
    accent-color: #4f46e5;
  }

  .choice-option label {
    margin: 0;
    font-weight: normal;
    line-height: 1.5;
    cursor: pointer;
  }

  /* Authored colour, size and family ride along as inline styles on nested
     spans, so nothing here may set those properties. */
  .choice-option-label p {
    display: inline;
    margin: 0;
  }

  .choice-option-label a {
    color: inherit;
    text-decoration: underline;
  }

  .choice-option-label sub,
  .choice-option-label sup {
    font-size: 75%;
    line-height: 0;
    position: relative;
  }

  .choice-option-label sup {
    top: -0.5em;
  }

  .choice-option-label sub {
    bottom: -0.25em;
  }
`;

interface ChoiceGroupData {
  field_name?: string;
  options?: ChoiceOption[];
  inline?: boolean;
}

interface ChoiceGroupProps {
  data: ChoiceGroupData;
  /* 'checkbox' allows several selections; 'radio' groups by field_name. */
  type: 'checkbox' | 'radio';
  read_only?: boolean;
  mutable?: boolean;
  defaultValue?: string[];
}

/* Replaces the library's own Checkboxes / RadioButtons so option labels can
   carry rich text — the library renders `option.text` as a plain string, so
   authored markup would show as literal characters.

   The element keeps its original `element` name ('Checkboxes' /
   'RadioButtons'), which means ReactFormGenerator._collect still takes its
   dedicated branch for these types: it reads `ref.options['child_ref_' + key]`
   off this component and checks each input's `checked`. That ref shape is
   therefore part of this component's contract with the library, not an
   internal detail — hence the class component and the exposed `options` map. */
export default class ChoiceGroupInput extends React.Component<ChoiceGroupProps> {
  /* Read by the library during submission. Keyed exactly as _collect expects. */
  options: Record<string, HTMLInputElement> = {};

  render(): JSX.Element {
    const { data, type, read_only: readOnly, mutable, defaultValue } = this.props;
    const options = data.options || [];

    return (
      <StyledWrapper className={data.inline ? 'is-inline' : ''}>
        {options.map((option) => {
          const domId = `fid_preview_${option.key}`;
          const checked =
            mutable && defaultValue !== undefined
              ? defaultValue.indexOf(option.key) > -1 ||
                defaultValue.indexOf(option.value) > -1
              : undefined;

          return (
            <div className="choice-option" key={`preview_${option.key}`}>
              <input
                id={domId}
                type={type}
                /* Checkboxes are independent, radios share one group. */
                name={
                  type === 'radio'
                    ? data.field_name
                    : `option_${option.key}`
                }
                value={option.value}
                defaultChecked={checked}
                disabled={readOnly ? true : undefined}
                ref={(c) => {
                  if (c && mutable) {
                    this.options[`child_ref_${option.key}`] = c;
                  }
                }}
              />
              <label htmlFor={domId}>
                <span
                  className="choice-option-label"
                  dangerouslySetInnerHTML={{ __html: labelMarkup(option) }}
                />
              </label>
            </div>
          );
        })}
      </StyledWrapper>
    );
  }
}

/* Only the radio variant is wired up. ReactFormGenerator's render switch
   resolves 'Checkboxes' from a module-scope binding captured at import time,
   so a checkbox equivalent could not be substituted from here. */
export class RadioGroupInput extends React.Component<
  Omit<ChoiceGroupProps, 'type'>
> {
  /* Forwarded so the library's _collect, which reads `options` off the ref it
     holds for this field, reaches the inner group. */
  inner: ChoiceGroupInput | null = null;

  get options(): Record<string, HTMLInputElement> {
    return this.inner ? this.inner.options : {};
  }

  render(): JSX.Element {
    return (
      <ChoiceGroupInput
        {...this.props}
        type="radio"
        ref={(c: ChoiceGroupInput | null) => {
          this.inner = c;
        }}
      />
    );
  }
}
